/* eslint-disable react-hooks/exhaustive-deps */
import React, {CSSProperties, Suspense, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import omit from 'lodash.omit';
import {useTranslation} from 'react-i18next';
import {
  AutoFillArgs,
  AutoFillResponse,
  ComputedPageItem,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {AutoFlowError, DetectionType, SERVER_ERROR_CODE} from '@taopix/taopix-core';
import {LoadingOverlay, MatchesMediaQuery, Theme} from '@taopix/taopix-design-system';
import classNames from 'classnames';
import useSettings from '../../../../Hooks/useSettings';
import {LayoutSelector, AssetServicesSelector, UserAssetSelector} from '../../../../Selectors';
import {AssetServicesActions} from '../../../Context/AssetServices/AssetServicesActions';
import {useAssetServices} from '../../../Context/AssetServices/AssetServicesContext';
import {useTaopix} from '../../../Context/TaopixContext';
import {UserFeedbackActions} from '../../../Context/UserFeedback/UserFeedbackActionTypes';
import {useUserFeedback} from '../../../Context/UserFeedback/UserFeedbackContext';
import {UserInterfaceActions} from '../../../Context/UserInterface/UserInterfaceActionTypes';
import {useUiState} from '../../../Context/UserInterface/UserInterfaceContext';
import {useExperience} from '../../../Experience/ExperienceProvider';
import {DragAndDropNames} from '../../../../Enums';
import Drag from '../../../DragAndDrop/Drag';
import {ToolPaletteSectionProps} from '../../../ToolPalette/ToolPaletteSection';
import ToolPalette from '../../../ToolPalette/ToolPalette';
import ToolPaletteSectionsContainer from '../../../ToolPalette/ToolPaletteSectionsContainer';
import AddPicturesButton from '../../../AssetServices/AddPicturesButton';

export type SidebarProps = {
  selectedItem: ComputedPageItem;
};

const PicturePalette = React.lazy(() => import('../../../Palette/PicturePalette'));
const TextStylePalette = React.lazy(() => import('../../../Palette/TextStylePalette'));
const StickerPalette = React.lazy(() => import('../../../Palette/StickerPalette'));
const FlightCheckPalette = React.lazy(
  () => import('../../../Palette/FlightCheck/FlightCheckPalette')
);

const Sidebar = ({selectedItem: item}: SidebarProps) => {
  // grab the app state
  const app = useTaopix();
  const settings = useSettings();
  const ui = useUiState();
  const userFeedback = useUserFeedback();
  const [showLoadingOverlay, setShowLoadingOverlay] = useState<boolean>(false);
  const [imagesLoading, setImagesLoading] = useState(false);
  const {t} = useTranslation();

  // pull in the experience settings
  const {experience} = useExperience();
  const services = useAssetServices();

  // get the layout from the store
  const productLayout = useSelector(LayoutSelector);

  // get the asset services
  const assetServices = useSelector(AssetServicesSelector);

  // get some props from the core
  const server = app.core.get('server');

  // grab the list of user asset from the store
  const assets = useSelector(UserAssetSelector);

  const onCloseAssetServiceList = () => {
    services.dispatch(
      AssetServicesActions.setOpenState({
        ...omit({...services.state}, ['assets', 'serviceDialogOpen']),
        open: false,
      })
    );
  };

  const toolPaletteStyle = 'floating';

  // Create the generic props for the tool palette sections
  const sectionProps: Partial<ToolPaletteSectionProps> = {
    showCloseButton: ui.state.isSmallScreen,
    buttonLayout: 'stacked',
    toolPaletteStyle: toolPaletteStyle,
  };

  const paletteDefaultProps = {
    item: item,
    sectionProps: sectionProps,
  };

  const onAutoFill = () => {
    if (experience.EDITOR.pictures.showAdvancedAutofill) {
      ui.dispatch(
        UserInterfaceActions.setAutoFillOptions({
          ...ui.state.autoFillOptions,
          autoFillDialog: true,
        })
      );
    } else {
      setShowLoadingOverlay(true);
      app.core
        .get('stateManager')
        .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', {
          save: true,
          showLoading: true,
          sortOrder: ui.state.palette.picturePallette.sortOrder,
          sortDirection: ui.state.palette.picturePallette.sortDirection,
          includeCovers: true,
          checkImageUsage: false,
          fromCreation: false,
          createPreviewThumbnail: true,
          coverItems: [],
        })
        .then(() => {
          setShowLoadingOverlay(false);
          ui.dispatch(
            UserInterfaceActions.setAutoFillOptions({
              autoFillCompleted: true,
            })
          );
        })
        .catch(error => {
          setShowLoadingOverlay(false);
          userFeedback.dispatch(
            UserFeedbackActions.setErrorUserFeedback({
              title: '',
              message:
                error instanceof AutoFlowError ? error.message : server.getErrorFromResponse(error),
              showHeader: SERVER_ERROR_CODE !== error.response?.status,
            })
          );
        });
    }
  };

  useEffect(() => {
    // Ensure focus mode is turned off if and when the tool palette gets reset
    if (ui.state.palette.section === null) {
      ui.dispatch(UserInterfaceActions.setViewOffset(0));
      ui.dispatch(UserInterfaceActions.setFocusMode(false));
      return;
    }
  }, [ui.state.palette.section]);

  const smallScreen = MatchesMediaQuery('(max-width: 799px)');

  // Open the pictures palette by default on large screen; nothing should be open on small screen
  useEffect(() => {
    // Check the screen size independently from the ui state (as this will always be null to start with)
    if (smallScreen) {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
    } else {
      ui.dispatch(UserInterfaceActions.setToolPaletteSection('pictures'));
    }
  }, [ui.state.isSmallScreen]);

  return (
    <div className="flex flex-col sm:order-1 lg:m-lg lg:mr-sm sm:mx-auto">
      <ToolPalette isOpen={true} toolPaletteStyle={toolPaletteStyle}>
        <Suspense>
          <Suspense>
            <AddPicturesButton
              close={onCloseAssetServiceList}
              onLoading={setImagesLoading}
              popoutUsesParentWidth
              buttonProps={{
                hideLabel: ui.state.palette.section !== null,
                id: 'mainAddPicturesButton',
                arrow: ui.state.palette.section === null ? 'down' : undefined,
              }}
              popoutProps={{
                // className: popoutClassNames,
                displayMode: ui.state.isSmallScreen ? 'modal' : 'positioned',
              }}
            />
          </Suspense>
        </Suspense>

        <Drag
          name={DragAndDropNames.PAGE}
          detectionType={DetectionType.AutoLayout}
          item={{}}
          passive={true}
        >
          <PicturePalette
            assetServices={assetServices}
            pictureUsage={
              Array.isArray(productLayout?.usagepicture) ? {} : productLayout?.usagepicture
            }
            sectionKey={'pictures'}
            authentication={settings.state.authentication}
            authenticationPrompt={() => {}}
            designerSettings={settings.state}
            selectableImages={false}
            server={server}
            assets={assets}
            isLoading={imagesLoading}
            onAutoFill={onAutoFill}
            {...paletteDefaultProps}
          />
        </Drag>

        <TextStylePalette sectionKey="text-styles" {...paletteDefaultProps} />

        <StickerPalette sectionKey={'stickers'} {...paletteDefaultProps} />

        <FlightCheckPalette sectionKey={'flight-check'} {...paletteDefaultProps} />
      </ToolPalette>
      <LoadingOverlay
        id={'sidebar-autoFill-busy-overlay'}
        open={showLoadingOverlay}
        label={t('*:str_MessagePleaseWait')}
        delay={500}
        key={'sidebar-autoFill-busy-overlay'}
      />
    </div>
  );
};

export default Sidebar;
